// Админка — пульт: людей и аккаунты правят в строках одной таблицы, команды — во второй вкладке.
// Каждое изменение — отдельная запись через apply_changes с проверкой версии, всё в истории.
// Только роль admin — маршрут и пункт меню тоже под ней. Спека: docs/superpowers/specs/2026-09-13-people-accounts-roles-design.md
import { useCallback, useMemo, useState } from 'react';
import { useIsAdmin } from '@core/auth/access';
import { data, useTable } from '@core/store';
import { Button } from '@core/ui/Button';
import { control } from '@core/ui/form';
import { SegmentedControl } from '@core/ui/SegmentedControl';
import { ScreenToolbar } from '../roadmap/shared/Toolbar';
import { useToast } from '../roadmap/shared/interaction';
import { PeopleTable } from './PeopleTable';
import { TeamsBlock } from './TeamsBlock';
import { OrderTab } from './OrderTab';
import { isProblem, unlinkedAccounts, type Runner } from './planner';

type Tab = 'people' | 'teams' | 'order';

export function AdminScreen() {
  const isAdmin = useIsAdmin();
  const profiles = useTable('profile');
  const people = useTable('person');
  const teams = useTable('team');
  const { fail } = useToast();
  const [tab, setTab] = useState<Tab>('people');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const unlinked = useMemo(() => unlinkedAccounts(profiles.values()).length, [profiles]);

  const run = useCallback<Runner>(async (planned) => {
    if (isProblem(planned)) { fail(planned.error); return false; }
    if (planned.changes.length === 0) return true;
    setBusy(true);
    const r = await data.applyChanges(planned.changes, planned.op);
    setBusy(false);
    if (!r.ok) { fail(r.error); return false; }
    return true;
  }, [fail]);
  const switchTab = (t: Tab) => { setTab(t); setAdding(false); };

  if (!isAdmin) return <div className="p-6 text-gray-600">Admin tools are for admins. Ask one of them if you need access.</div>;
  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow border border-gray-300 overflow-hidden" data-admin>
      <ScreenToolbar
        actions={tab !== 'order' && <Button size="sm" onClick={() => setAdding(true)} disabled={adding}>{tab === 'people' ? '+ Person' : '+ Team'}</Button>}
        what={<SegmentedControl<Tab> role="tabs" label="Admin sections" value={tab} onChange={switchTab}
          items={[{ key: 'people', label: 'People', count: people.size }, { key: 'teams', label: 'Teams', count: teams.size }, { key: 'order', label: 'Order', title: 'Default project order for people who have not reordered rows themselves' }]} />}
        filter={tab !== 'order' && <input type="search" className={`${control} w-64`} placeholder={tab === 'people' ? 'Search name, email, account…' : 'Search teams…'} aria-label="Search"
          data-admin-search value={query} onChange={(e) => setQuery(e.target.value)} />}
        status={tab === 'people' && unlinked > 0 && <span className="text-sm text-gray-600 whitespace-nowrap" data-admin-unlinked={unlinked}><span className="font-semibold text-gray-900">{unlinked}</span> {unlinked === 1 ? 'account' : 'accounts'} unlinked</span>}
      />
      <div className="flex-1 min-h-0 overflow-auto px-4 py-3">
        {tab === 'people' && <PeopleTable query={query} run={run} busy={busy} adding={adding} onAdded={() => setAdding(false)} />}
        {tab === 'teams' && <TeamsBlock query={query} run={run} busy={busy} adding={adding} onAdded={() => setAdding(false)} />}
        {tab === 'order' && <OrderTab run={run} busy={busy} />}
      </div>
    </div>
  );
}
